import React from 'react';
import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  MountableDialog
} from '@oysterjs/ui/dialog';
import { Description, ErrorMessage, Field, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';

import { type MailboxProvider } from '~/api/graphql';
import {
  formatSendTime,
  formatSendTimeWithoutZone,
  fromDateTimeInputs,
  type ScheduleQuickPick,
  pickedSendTimeErrorMessage,
  scheduleQuickPicks,
  scheduledEmailHelper,
  timeZoneDescription,
  toDateInputValue,
  toTimeInputValue
} from '~/lib/scheduled-send';

const CUSTOM_TIME = 'custom';

/**
 * Picks a send time: Schedule send for an email that isn't scheduled yet, and
 * Reschedule for one that is. Mounted only while open, so every opening starts
 * from a fresh time.
 */
export const ScheduleSendDialog = (props: {
  mode: 'schedule' | 'reschedule';
  currentSendAt?: Date | null;
  fromAddress?: string;
  provider?: MailboxProvider;
  submitting: boolean;
  error: string | null;
  onSubmit: (sendAt: Date) => void;
  onClose: () => void;
  /** The quick pick chosen when the dialog opens; the first one's time otherwise. */
  initialQuickPick?: ScheduleQuickPick['label'];
  /** A time of its own to open on instead of a quick pick's. */
  initialSendAt?: Date;
  /** Wording for scheduling many emails at once; one email's wording otherwise. */
  batchWording?: {
    title: string;
    submitLabel: string;
    errorTitle: string;
    /** Names the time the first email goes out, as in "Starts Tue Oct 13, 8:00 AM PDT". */
    sendTimeLabel: string;
    /** Named in the too-soon message, as the way to send sooner. */
    sendNowLabel?: string;
    /** Replaces the too-soon message, for a picker with nothing to send sooner with. */
    tooSoonMessage?: string;
    footnote: string;
  };
}) => {
  const isReschedule = props.mode === 'reschedule';
  const [openedAt] = React.useState(() => new Date());
  const quickPicks = React.useMemo(() => scheduleQuickPicks(openedAt), [openedAt]);
  const initialQuickPick = quickPicks.find(({ label }) => label === props.initialQuickPick);
  const initialSendAt =
    props.currentSendAt && props.currentSendAt > openedAt
      ? props.currentSendAt
      : (props.initialSendAt ?? (initialQuickPick ?? quickPicks[0]).sendAt);
  const [choice, setChoice] = React.useState<string>(initialQuickPick?.label ?? CUSTOM_TIME);
  const [dateValue, setDateValue] = React.useState(toDateInputValue(initialSendAt));
  const [timeValue, setTimeValue] = React.useState(toTimeInputValue(initialSendAt));

  const sendAt = fromDateTimeInputs(dateValue, timeValue);
  const sendTimeError = sendAt
    ? pickedSendTimeErrorMessage(
        sendAt,
        new Date(),
        props.batchWording?.sendNowLabel,
        props.batchWording?.tooSoonMessage
      )
    : 'Pick a date and a time to send it.';
  const canSubmit = !!sendAt && !sendTimeError && !props.submitting;
  // A missing or past day is the date's fault; anything else is the time's.
  // The error sits in the invalid input's Field, which describes that input by it.
  const isDateInvalid = !!sendTimeError && (!dateValue || dateValue < toDateInputValue(new Date()));
  const isTimeInvalid = !!sendTimeError && !isDateInvalid;
  const errorMessage = sendTimeError && <ErrorMessage role="alert">{sendTimeError}</ErrorMessage>;

  const pickQuickTime = (label: string) => {
    setChoice(label);
    const picked = quickPicks.find((quickPick) => quickPick.label === label);
    if (!picked) return;
    setDateValue(toDateInputValue(picked.sendAt));
    setTimeValue(toTimeInputValue(picked.sendAt));
  };

  const editDate = (value: string) => {
    setChoice(CUSTOM_TIME);
    setDateValue(value);
  };

  const editTime = (value: string) => {
    setChoice(CUSTOM_TIME);
    setTimeValue(value);
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (canSubmit && sendAt) props.onSubmit(sendAt);
  };

  const submitLabel = props.submitting
    ? isReschedule
      ? 'Rescheduling…'
      : 'Scheduling…'
    : props.error
      ? 'Try again'
      : isReschedule
        ? 'Reschedule'
        : (props.batchWording?.submitLabel ?? 'Schedule send');

  return (
    <MountableDialog onClose={props.submitting ? () => {} : props.onClose} size="md">
      <form onSubmit={submit}>
        <DialogTitle>
          {isReschedule ? 'Reschedule' : (props.batchWording?.title ?? 'Schedule send')}
        </DialogTitle>
        <DialogBody className="space-y-4">
          {props.error && (
            <div
              role="alert"
              className="rounded-md bg-red-50 dark:bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300"
            >
              <p className="font-medium">
                {isReschedule
                  ? "We couldn't reschedule this email."
                  : (props.batchWording?.errorTitle ?? "We couldn't schedule this email.")}
              </p>
              <p className="mt-0.5">{props.error}</p>
            </div>
          )}
          {isReschedule && props.currentSendAt && (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Currently: {formatSendTime(props.currentSendAt)}
            </p>
          )}
          {!isReschedule && (
            <RadioGroup value={choice} onChange={pickQuickTime} aria-label="When to send">
              {quickPicks.map((quickPick) => (
                <RadioField key={quickPick.label}>
                  <Radio value={quickPick.label} color="sky" />
                  <Label>
                    {quickPick.label}{' '}
                    <span className="text-neutral-500 dark:text-neutral-400">
                      {formatSendTimeWithoutZone(quickPick.sendAt)}
                    </span>
                  </Label>
                </RadioField>
              ))}
              <RadioField>
                <Radio value={CUSTOM_TIME} color="sky" />
                <Label>Pick a date and time</Label>
              </RadioField>
            </RadioGroup>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field>
              <Label>Date</Label>
              <Input
                type="date"
                value={dateValue}
                min={toDateInputValue(openedAt)}
                invalid={isDateInvalid}
                onChange={(event) => editDate(event.target.value)}
              />
              {isDateInvalid && errorMessage}
            </Field>
            <Field>
              <Label>Time</Label>
              <Input
                type="time"
                value={timeValue}
                invalid={isTimeInvalid}
                onChange={(event) => editTime(event.target.value)}
              />
              {isTimeInvalid && errorMessage}
            </Field>
          </div>
          <Field>
            {!sendTimeError && sendAt && (
              <p className="text-sm font-medium text-neutral-900 dark:text-white">
                {props.batchWording?.sendTimeLabel ?? 'Sends'} {formatSendTime(sendAt)}
              </p>
            )}
            <Description>
              {timeZoneDescription(sendAt ?? openedAt)}, your time zone.
              {props.fromAddress &&
                ` You can close Cara and it still goes out from ${props.fromAddress}.`}
            </Description>
          </Field>
          {!isReschedule && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {props.batchWording?.footnote ?? scheduledEmailHelper(props.provider)}
            </p>
          )}
        </DialogBody>
        <DialogActions>
          <Button color="white" disabled={props.submitting} onClick={props.onClose}>
            Cancel
          </Button>
          <Button type="submit" color="sky" disabled={!canSubmit} loading={props.submitting}>
            {submitLabel}
          </Button>
        </DialogActions>
      </form>
    </MountableDialog>
  );
};

/**
 * The one way to stop a scheduled email. It becomes a normal draft again, and
 * its linked Outlook draft, if any, stays as it is.
 */
export const CancelScheduleDialog = (props: {
  open: boolean;
  cancelling: boolean;
  /** The server's refusal of the last confirm, shown until the dialog closes. */
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) => (
  <Dialog open={props.open} onClose={props.cancelling ? () => {} : props.onClose} size="md">
    <DialogTitle>Cancel this scheduled email?</DialogTitle>
    <DialogBody className="space-y-4">
      {props.error && (
        <div
          role="alert"
          className="rounded-md bg-red-50 dark:bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          <p className="font-medium">We couldn't cancel this email.</p>
          <p className="mt-0.5">{props.error}</p>
        </div>
      )}
      <p className="text-sm text-neutral-700 dark:text-neutral-300">
        It won't be sent. It becomes a draft again, so you can edit, send or schedule it later.
      </p>
    </DialogBody>
    <DialogActions>
      <Button color="white" disabled={props.cancelling} onClick={props.onClose}>
        Keep it scheduled
      </Button>
      <Button color="red" loading={props.cancelling} onClick={props.onConfirm}>
        Cancel send
      </Button>
    </DialogActions>
  </Dialog>
);
